import { notification } from '@/components/Notification';
import { apiErrorHandler } from '@/utils/app';
import { isNumber } from '@/utils/common';
import { IS_POS } from '@/utils/env';
import { playSound } from '@/utils/sound';
import { App, Modal as ModalNative } from 'antd';
import { ModalFuncProps, ModalProps } from 'antd/es/modal';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import React, { useEffect, useState } from 'react';

type Props = {} & ModalProps;

let zIndex = 1001;

let notificationContainer: HTMLElement = null;
export const getZIndex = () => {
  // 更新 notification 的 zIndex，否则无法展示在 modal 上
  if (!notificationContainer) {
    notificationContainer = document.createElement('div');
    notificationContainer.style.position = 'relative';
    document.body.appendChild(notificationContainer);
    notification.config({
      getContainer: () => notificationContainer,
    });
  }
  notificationContainer.style.zIndex = `${zIndex}`;

  return ++zIndex;
};

const OriginModal: React.FC<Props> = ({ onOk = () => {}, onCancel = () => {}, zIndex, ...modalProps }: Props) => {
  const [keepedZIndex, setKeepedZIndex] = useState(zIndex ?? getZIndex());

  useEffect(() => {
    if (isNumber(zIndex)) {
      setKeepedZIndex(zIndex);
    }
  }, [zIndex]);

  const animProps = IS_POS
    ? {
        transitionName: '',
        maskTransitionName: '',
      }
    : {};
  return (
    <ModalNative
      zIndex={keepedZIndex}
      // zIndex={1000}
      {...animProps}
      {...modalProps}
      onOk={async (...args) => {
        playSound('click');
        if (onOk) {
          await onOk(...args);
        }
      }}
      onCancel={(...args) => {
        playSound('click');
        onCancel(...args);
      }}
    />
  );
};

const Modal = OriginModal as typeof ModalNative;

// 全局单例 api
let modalInstance: ReturnType<(typeof App)['useApp']>['modal'];
let readyResolve: ((api: typeof modalInstance) => void) | null = null;
const readyPromise: Promise<typeof modalInstance> = new Promise(res => {
  readyResolve = res;
});
export const ModalHolder = () => {
  const { modal } = App.useApp();
  useEffect(() => {
    modalInstance = modal;
    // resolve 等待者
    readyResolve?.(modal);
    return () => {
      modalInstance = null;
    };
  }, [modal]);
  return <></>;
};

type KeyOfModalStaticFn = keyof ModalStaticFunctions;
type ModalFnReturn<T extends KeyOfModalStaticFn> = ReturnType<ModalStaticFunctions[T]>;
const modalFn = <T extends KeyOfModalStaticFn>(
  method: keyof ModalStaticFunctions,
  opts: ModalFuncProps
): ModalFnReturn<T> => {
  const { onCancel, onOk, okButtonProps = {}, cancelButtonProps = {}, ...restProps } = opts;
  const componentSize = IS_POS ? 'large' : 'middle';

  return modalInstance[method]({
    zIndex: getZIndex(),
    ...restProps,
    okButtonProps: {
      ...okButtonProps,
      size: componentSize,
    },
    cancelButtonProps: {
      ...cancelButtonProps,
      size: componentSize,
    },
    onCancel: (...args) => {
      playSound('click');
      if (onCancel) {
        onCancel(...args);
      }
    },
    onOk: async (...args) => {
      playSound('click');
      if (onOk) {
        try {
          await onOk(...args);
        } catch (e) {
          // 这里的异常无法全局捕获，手动调用
          apiErrorHandler(e);
          // 抛出异常让 modal 感知
          throw e;
        }
      }
    },
  });
  // };
};

Modal.confirm = (opts: ModalFuncProps) => modalFn('confirm', opts);
Modal.info = (opts: ModalFuncProps) => modalFn('info', opts);
Modal.success = (opts: ModalFuncProps) => modalFn('success', opts);
Modal.error = (opts: ModalFuncProps) => modalFn('error', opts);
Modal.warning = (opts: ModalFuncProps) => modalFn('warning', opts);
Modal.destroyAll = ModalNative.destroyAll;
Modal.config = ModalNative.config;
delete Modal.warn;

export default Modal;
